import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, UserCog } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { del, get, patch, post, qs } from '../api';
import { useMe, usePermissions } from '../auth';
import { Confirm, Empty, ErrorBox, Loading, Modal, PageHeader, Pagination, useToast } from '../components/ui';
import { ROLE_HELP, ROLE_LABEL, fmtRelative } from '../format';
import type { Page, Role, User } from '../types';
import { groupName, useGroups } from './shared';

const LIMIT = 50;

export function UsersPage() {
  const perms = usePermissions();
  const { user: me } = useMe();
  const groups = useGroups();
  const [groupId, setGroupId] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const users = useQuery({
    queryKey: ['users', { groupId, offset }],
    queryFn: () => get<Page<User>>(`/users${qs({ groupId, offset, limit: LIMIT })}`),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Usuarios"
        subtitle={perms.isSuper ? 'Usuarios de todos los grupos' : 'Usuarios de tu grupo'}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Plus size={16} /> Nuevo usuario
          </button>
        }
      />
      <div className="card">
        {perms.isSuper && (
          <div className="toolbar">
            <select className="select" value={groupId} onChange={(e) => (setGroupId(e.target.value), setOffset(0))}>
              <option value="">Todos los grupos</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <ErrorBox error={users.error} />
        {users.isLoading ? (
          <Loading />
        ) : !users.data?.items.length ? (
          <Empty icon={UserCog} title="No hay usuarios" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Rol</th>
                  {perms.isSuper && <th>Grupo</th>}
                  <th>Estado</th>
                  <th>Último acceso</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.items.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="cell-main">{u.name}</div>
                      <div className="cell-sub">{u.email}</div>
                    </td>
                    <td>
                      <span className={`badge ${u.role === 'superadmin' || u.role === 'admin' ? 'badge-accent' : ''}`}>{ROLE_LABEL[u.role]}</span>
                    </td>
                    {perms.isSuper && <td>{u.role === 'superadmin' ? <span className="faint">—</span> : groupName(groups.data, u.groupId)}</td>}
                    <td>
                      {u.active ? <span className="badge badge-done">Activo</span> : <span className="badge badge-cancelled">Desactivado</span>}
                      {u.mustChangePassword && <div className="cell-sub">Debe cambiar la contraseña</div>}
                    </td>
                    <td>{fmtRelative(u.lastLoginAt)}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-icon" onClick={() => setEditing(u)} aria-label="Editar">
                          <Pencil size={16} />
                        </button>
                        {u.id !== me.id && (
                          <button className="btn btn-icon danger" onClick={() => setDeleting(u)} aria-label="Eliminar">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {users.data && <Pagination offset={offset} limit={LIMIT} total={users.data.total} onChange={setOffset} />}
      </div>

      {editing && <UserModal user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {deleting && (
        <Confirm
          title="Eliminar usuario"
          message={
            <>
              ¿Eliminar a <strong>{deleting.name}</strong> ({deleting.email})? Sus trabajos se conservan. Si solo quieres
              impedir el acceso, desactívalo en su lugar.
            </>
          }
          confirmLabel="Eliminar"
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await del(`/users/${deleting.id}`);
            await qc.invalidateQueries({ queryKey: ['users'] });
            toast('Usuario eliminado');
          }}
        />
      )}
    </>
  );
}

function UserModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const perms = usePermissions();
  const { user: me } = useMe();
  const groups = useGroups();
  const qc = useQueryClient();
  const toast = useToast();

  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'user');
  const [groupId, setGroupId] = useState<string>(user?.groupId?.toString() ?? (perms.isSuper ? '' : String(me.groupId)));
  const [active, setActive] = useState(user?.active ?? true);
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(user?.mustChangePassword ?? true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const roles: Role[] = perms.isSuper ? ['viewer', 'user', 'admin', 'superadmin'] : ['viewer', 'user', 'admin'];
  const isSelf = user?.id === me.id;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const gid = role === 'superadmin' ? null : groupId ? Number(groupId) : undefined;
      if (user) {
        await patch(`/users/${user.id}`, {
          name,
          ...(!isSelf && { role, active }),
          ...(perms.isSuper && !isSelf && { groupId: gid ?? null }),
          ...(password && { password, mustChangePassword: mustChange }),
        });
      } else {
        await post('/users', { email, name, role, groupId: perms.isSuper ? gid : undefined, password, mustChangePassword: mustChange });
      }
      await qc.invalidateQueries({ queryKey: ['users'] });
      toast(user ? 'Usuario actualizado' : 'Usuario creado');
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={user ? 'Editar usuario' : 'Nuevo usuario'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary" form="user-form" disabled={busy}>
            {busy && <Loader2 size={14} className="spin" />}
            Guardar
          </button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit} autoComplete="off">
        <ErrorBox error={error} />
        <div className="field">
          <label htmlFor="u-name">Nombre</label>
          <input id="u-name" className="input" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label htmlFor="u-email">Email</label>
          <input id="u-email" className="input" type="email" required disabled={!!user} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="u-role">Rol</label>
            <select id="u-role" className="select" value={role} disabled={isSelf} onChange={(e) => setRole(e.target.value as Role)}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            <span className="hint">{ROLE_HELP[role]}</span>
          </div>
          {perms.isSuper && role !== 'superadmin' && (
            <div className="field">
              <label htmlFor="u-group">Grupo</label>
              <select id="u-group" className="select" required value={groupId} disabled={isSelf} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">Elige un grupo</option>
                {groups.data?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="u-password">{user ? 'Nueva contraseña' : 'Contraseña inicial'}</label>
          <input
            id="u-password"
            className="input"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required={!user}
            placeholder={user ? 'Dejar vacío para no cambiarla' : 'Mínimo 10 caracteres'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {(!user || password) && (
          <label className="checkbox">
            <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
            <span>Pedir que la cambie al entrar</span>
          </label>
        )}
        {user && !isSelf && (
          <label className="checkbox">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>
              Usuario activo<span className="faint"> — desactivarlo cierra sus sesiones</span>
            </span>
          </label>
        )}
      </form>
    </Modal>
  );
}
